import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { GENERIC_API_ERROR_MESSAGE } from '../../../../../constants/errorMessage';
import { ACCOUNT_ERROR_RESPONSE_CODES, PASSWORD_REGEX } from '../../../../../constants/constants';
import { useUpdatePasswordMutation } from '../../../../../data/customer';
import { PolicyHolderPropTypes } from '../../../../../propTypes/petDetails';
import { FORM_KEYS } from '../../../../../constants/formKeys';
import { SLICES } from '../../../../../constants/slices';

const usePasswordDetailsDialog = ({ fieldFormContext, policyHolder, setShowSuccessMessage, onClose }) => {
  const { trigger } = useFormContext();
  const { email } = policyHolder;
  const [updatePassword] = useUpdatePasswordMutation();
  const { tokenClaims, authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);

  const [errors, setErrors] = useState({
    isInvalidPassword: false,
    isInvalidNewPassword: false,
    isInvalidConfirmPassword: false,
    isInvalidAccountResponse: false,
    invalidAccountResponseMessage: '',
    isPasswordLengthErrorMessageDisplayed: false,
    isPasswordUppercaseErrorMessageDisplayed: false,
    isPasswordDigitErrorMessageDisplayed: false,
    isPasswordNotPasswordErrorMessageDisplayed: false
  });

  const resetErrors = () => {
    setErrors({
      isInvalidPassword: false,
      isInvalidNewPassword: false,
      isInvalidConfirmPassword: false,
      isInvalidAccountResponse: false,
      invalidAccountResponseMessage: '',
      isPasswordLengthErrorMessageDisplayed: false,
      isPasswordUppercaseErrorMessageDisplayed: false,
      isPasswordDigitErrorMessageDisplayed: false,
      isPasswordNotPasswordErrorMessageDisplayed: false
    });
  };

  const handleUpdate = async () => {
    const formValid = await trigger([FORM_KEYS.NEW_PASSWORD, FORM_KEYS.CURRENT_PASSWORD, FORM_KEYS.CONFIRM_PASSWORD], {
      shouldFocus: true
    });

    if (formValid) {
      const { currentPassword, newPassword, confirmPassword } = fieldFormContext.getValues();

      if (confirmPassword !== newPassword) {
        setErrors((prevState) => ({
          ...prevState,
          isInvalidConfirmPassword: true
        }));
      } else {
        updatePasswordHandler(currentPassword, newPassword);
      }
    }
  };

  const updatePasswordHandler = async (currentPassword, newPassword) => {
    const emailAddress = email;
    const userId = tokenClaims?.sub;
    const brandPartner = authDetails?.partnerNumber;
    const updatedPassword = {
      email: emailAddress,
      userId,
      password: newPassword,
      oldPassword: currentPassword,
      brandPartner
    };
    try {
      const res = await updatePassword(updatedPassword);
      if (res?.data.isSuccessResponse) {
        resetErrors();
        fieldFormContext.reset();
        onClose();
        setShowSuccessMessage(res?.data.isSuccessResponse);
      } else {
        if (res?.data.message === ACCOUNT_ERROR_RESPONSE_CODES.INCORRECT_PASSWORD) {
          setErrors((prevState) => ({
            ...prevState,
            isInvalidPassword: true
          }));
        }
        if (res?.data.message === ACCOUNT_ERROR_RESPONSE_CODES.USED_OLD_PW) {
          setErrors((prevState) => ({
            ...prevState,
            isInvalidNewPassword: true
          }));
        }
        if (res?.data.statusCode === ACCOUNT_ERROR_RESPONSE_CODES.BLOCKED_ACCOUNT_STATUS_CODE) {
          setErrors((prevState) => ({
            ...prevState,
            isInvalidAccountResponse: true,
            invalidAccountResponseMessage: res?.data.message
          }));
        }
      }
    } catch (error) {
      setErrors((prevState) => ({
        ...prevState,
        isInvalidAccountResponse: true,
        invalidAccountResponseMessage: GENERIC_API_ERROR_MESSAGE
      }));
    }
  };

  const currentPasswordOnChange = (event) => {
    setErrors((prevState) => ({
      ...prevState,
      isInvalidPassword: false
    }));
  };

  const newPasswordOnChange = (event) => {
    const newPassword = fieldFormContext.getValues()[FORM_KEYS.NEW_PASSWORD];
    setErrors((prevState) => ({
      ...prevState,
      isInvalidNewPassword: false,
      isPasswordLengthErrorMessageDisplayed: false,
      isPasswordUppercaseErrorMessageDisplayed: false,
      isPasswordDigitErrorMessageDisplayed: false,
      isPasswordNotPasswordErrorMessageDisplayed: false
    }));

    if (newPassword !== '') {
      if (!PASSWORD_REGEX.PASSWORD_MUST_BE_MIN_8_CHARACTERS.test(newPassword)) {
        setErrors((prevState) => ({
          ...prevState,
          isPasswordLengthErrorMessageDisplayed: true
        }));
      }
      if (!PASSWORD_REGEX.PASSWORD_MUST_CONTAIN_UPPERCASE.test(newPassword)) {
        setErrors((prevState) => ({
          ...prevState,
          isPasswordUppercaseErrorMessageDisplayed: true
        }));
      }
      if (!PASSWORD_REGEX.PASSWORD_MUST_CONTAIN_DIGIT.test(newPassword)) {
        setErrors((prevState) => ({
          ...prevState,
          isPasswordDigitErrorMessageDisplayed: true
        }));
      }
      if (newPassword.toLowerCase().indexOf('password') >= 0) {
        setErrors((prevState) => ({
          ...prevState,
          isPasswordNotPasswordErrorMessageDisplayed: true
        }));
      }
    }
  };

  const confirmPasswordOnChange = (event) => {
    setErrors((prevState) => ({
      ...prevState,
      isInvalidConfirmPassword: false
    }));
  };

  return {
    handleUpdate,
    updatePasswordHandler,
    currentPasswordOnChange,
    newPasswordOnChange,
    confirmPasswordOnChange,
    errors
  };
};

usePasswordDetailsDialog.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  fieldFormContext: PropTypes.any,
  policyHolder: PolicyHolderPropTypes,
  setShowSuccessMessage: PropTypes.func,
  onClose: PropTypes.func
};

export default usePasswordDetailsDialog;
